'use client';

import { Box, Button, Center, List, Separator, Spinner, Stack } from '@chakra-ui/react';
import { NavigateBar } from '../components/NavigateBar';
import { _Bool, useStore } from '../store';
import { Request } from '../helper/Request';
import {
    DialogActionTrigger,
    DialogBody,
    DialogCloseTrigger,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogRoot,
    DialogTitle,
} from '../components/ui/dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataListItem, DataListRoot } from '../components/ui/data-list';
import { Text } from '../components/Text';

type SubmitResponse = {
    error?: string;
};

const apiErrorKeyMap: Record<string, string> = {
    'Invalid email: empty value is not allowed.': 'submitScreen.apiErrors.invalidEmailEmpty',
    'Invalid bank: empty value is not allowed.': 'submitScreen.apiErrors.invalidBankEmpty',
    'Invalid since: empty value is not allowed.': 'submitScreen.apiErrors.invalidSinceEmpty',
    'Invalid since: must be a valid date.': 'submitScreen.apiErrors.invalidSinceDate',
    'Invalid files: at least one file is required.': 'submitScreen.apiErrors.invalidFilesRequired',
};

export const Submit = (props: any) => {
    const { t } = useTranslation();
    const {
        email,
        bank,
        since,
        oldestTransactionDate,
        sessionWarning,
        allowCredentials,
        allowTesting,
        twoFaAvailable,
        twoFaAvailableTransaction,
        captchaDetection,
        twoFaList,
        note,
        fileStore,
    } = useStore();
    const [showDialog, setShowDialog] = useState(false);
    const [showUploadProgress, setShowUploadProgress] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState<boolean | undefined>(undefined);
    const [dialogMessage, setDialogMessage] = useState<string | undefined>(undefined);

    const readAbleBoolean = (value: _Bool): string => {
        return value === 'true' ? t('yes') : t('no');
    };

    const getApiErrorMessage = (apiError?: string): string => {
        if (!apiError) return t('submitScreen.sendingError');

        const translationKey = apiErrorKeyMap[apiError];
        if (!translationKey) return apiError;

        return t(translationKey);
    };

    const submitData = async (): Promise<void> => {
        const hasEmail = email.trim().length > 0;
        const hasBank = bank.trim().length > 0;
        const hasSince = since.trim().length > 0;
        const hasValidSinceDate = hasSince && !Number.isNaN(new Date(since).getTime());
        const hasOldestTransactionDate = oldestTransactionDate.trim().length > 0;
        const hasValidOldestTransactionDate =
            !hasOldestTransactionDate || !Number.isNaN(new Date(oldestTransactionDate).getTime());
        const hasFiles = fileStore.some((file) => file.name.trim().length > 0);

        if (!hasEmail || !hasBank || !hasSince || !hasFiles) {
            const missingFields: string[] = [];
            if (!hasEmail) missingFields.push(t('generalScreen.email'));
            if (!hasBank) missingFields.push(t('generalScreen.bank'));
            if (!hasSince) missingFields.push(t('generalScreen.sinceDate'));
            if (!hasFiles) missingFields.push(t('submitScreen.addedFiles'));

            setUploadSuccess(false);
            setDialogMessage(t('submitScreen.requiredFieldsError', { fields: missingFields.join(', ') }));
            setShowDialog(true);
            return;
        }

        if (!hasValidSinceDate) {
            setUploadSuccess(false);
            setDialogMessage(t('submitScreen.apiErrors.invalidSinceDate'));
            setShowDialog(true);
            return;
        }

        if (!hasValidOldestTransactionDate) {
            setUploadSuccess(false);
            setDialogMessage(t('submitScreen.apiErrors.invalidOldestTransactionDate'));
            setShowDialog(true);
            return;
        }

        setDialogMessage(undefined);
        setShowUploadProgress(true);
        try {
            const response = await Request.post<SubmitResponse>({
                path: '/har',
                data: {
                    email,
                    bank,
                    since,
                    oldestTransactionDate,
                    sessionWarning: sessionWarning === 'true',
                    allowCredentials: allowCredentials === 'true',
                    allowTesting: allowTesting === 'true',
                    twoFaAvailable: twoFaAvailable === 'true',
                    twoFaAvailableTransaction: twoFaAvailableTransaction === 'true',
                    captchaDetection: captchaDetection === 'true',
                    twoFaList,
                    note,
                    files: fileStore,
                },
            });
            if (response.status === 200) {
                setUploadSuccess(true);
            } else {
                setUploadSuccess(false);
                console.error('Error submitting data:', response.data?.error);
                setDialogMessage(getApiErrorMessage(response.data?.error));
            }
        } catch (error) {
            const requestError = error as {
                response?: {
                    data?: SubmitResponse;
                };
            };

            setDialogMessage(getApiErrorMessage(requestError.response?.data?.error));
            setUploadSuccess(false);
        } finally {
            setShowUploadProgress(false);
        }
        setShowDialog(true);
    };
    return (
        <>
            {showUploadProgress && (
                <Box pos="absolute" inset="0" bg="bg/80">
                    <Center h="full" direction="row">
                        <Box>
                            <Center mb={4}>
                                <Spinner size="xl" color="teal.500" />
                            </Center>
                            <Text trans="submitScreen.uploadingProgress" />
                        </Box>
                    </Center>
                </Box>
            )}
            <Stack gap="4">
                <DataListRoot size="md">
                    <DataListItem label={t('generalScreen.email')} value={email} />
                </DataListRoot>
                <DataListRoot size="md">
                    <DataListItem label={t('generalScreen.bank')} value={bank} />
                </DataListRoot>
                <Separator />
                <DataListRoot size="md">
                    <DataListItem label={t('generalScreen.sinceDate')} value={since} />
                </DataListRoot>
                <DataListRoot size="md">
                    <DataListItem label={t('generalScreen.oldestDate')} value={oldestTransactionDate} />
                </DataListRoot>
                <Separator />
                <DataListRoot size="md">
                    <DataListItem label={t('generalScreen.twoFaAvailable')} value={readAbleBoolean(twoFaAvailable)} />
                </DataListRoot>
                <DataListRoot size="md">
                    <DataListItem
                        label={t('generalScreen.twoFaAvailableTransaction')}
                        value={readAbleBoolean(twoFaAvailableTransaction)}
                    />
                </DataListRoot>
                <DataListRoot size="md">
                    <DataListItem
                        label={t('generalScreen.captchaDetection')}
                        value={readAbleBoolean(captchaDetection)}
                    />
                </DataListRoot>
                <DataListRoot size="md">
                    <DataListItem label={t('generalScreen.twoFaList')} value={twoFaList} />
                </DataListRoot>
                <Separator />
                <DataListRoot size="md">
                    <DataListItem label={t('generalScreen.sessionWaring')} value={readAbleBoolean(sessionWarning)} />
                </DataListRoot>
                <DataListRoot size="md">
                    <DataListItem label={t('generalScreen.credentials')} value={readAbleBoolean(allowCredentials)} />
                </DataListRoot>
                <DataListRoot size="md">
                    <DataListItem label={t('generalScreen.testing')} value={readAbleBoolean(allowTesting)} />
                </DataListRoot>
                <Separator />
                <DataListRoot size="md">
                    <DataListItem label={t('generalScreen.note')} value={note} />
                </DataListRoot>
                <Separator />
                <DataListRoot size="md">
                    <DataListItem
                        label={t('submitScreen.addedFiles')}
                        value={
                            <List.Root textStyle="sm" marginLeft={4}>
                                {fileStore.map((item) => {
                                    return <List.Item>{item.name}</List.Item>;
                                })}
                            </List.Root>
                        }
                    />
                </DataListRoot>
            </Stack>
            <DialogRoot size="md" open={showDialog} motionPreset="slide-in-bottom">
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('submitScreen.dialogTitle')}</DialogTitle>
                    </DialogHeader>
                    <DialogBody>
                        {dialogMessage ?? (uploadSuccess ? t('submitScreen.sendingSuccess') : t('submitScreen.sendingError'))}
                    </DialogBody>
                    <DialogFooter>
                        <DialogActionTrigger asChild>
                            <Button
                                onClick={() => {
                                    setShowDialog(false);
                                    setUploadSuccess(undefined);
                                    setDialogMessage(undefined);
                                }}
                            >
                                {t('ok')}
                            </Button>
                        </DialogActionTrigger>
                    </DialogFooter>
                    <DialogCloseTrigger />
                </DialogContent>
            </DialogRoot>
            <NavigateBar
                mt={10}
                backPath="/fileupload"
                continueButtonText={t('submit')}
                continueButtonColor="black"
                overloadContinueFn={async () => await submitData()}
            />
        </>
    );
};
